<template>
  <div class="sp-freelancer-form">
    <v-form ref="form">
      <v-text-field
        v-model="yearsOfExperience"
        :rules="[rules.isNumber, rules.required]"
        :label="$t('sp.registration.company.services.years.of.experience')"
        :placeholder="$t('sp.registration.company.services.years.of.experience.placeholder')"
        dense
        outlined
        required
      />
      <div class="company-services-sub-title">
        {{ $t('sp.registration.company.services.select.services') }}
      </div>

      <v-list
        flat
        class="services-list"
      >
        <v-list-group
          v-for="service in topLevelServicesWithSubs"
          :key="service"
          :append-icon="''"
        >
          <template v-slot:activator>
            <v-list-item-icon>
              <v-icon color="#E7E9EC" small>add</v-icon>
            </v-list-item-icon>
            <v-list-item-content>
              <v-list-item-title v-text="$t(`${service}`)"></v-list-item-title>
            </v-list-item-content>
          </template>

          <v-list-item
            v-for="subService in formattedServicesOptions[service]"
            :key="subService"
          >
            <template>
              <v-list-item-action>
                <custom-checkbox
                  color="cold-marine"
                  size="s"
                  :value="flatServicesOptionsObject[subService]"
                  @input="onCheck(subService)"
                ></custom-checkbox>
              </v-list-item-action>

              <v-list-item-content>
                <v-list-item-title v-text="$t(`${subService}`)"></v-list-item-title>
              </v-list-item-content>
            </template>
          </v-list-item>
        </v-list-group>
        <v-list-item
          v-for="service in topLevelServicesWithoutSubs"
          :key="service"
        >
          <template>
            <v-list-item-action>
              <custom-checkbox
                :value="flatServicesOptionsObject[service]"
                color="cold-marine"
                @input="onCheck(service)"
                size="s"
              ></custom-checkbox>
            </v-list-item-action>

            <v-list-item-content>
              <v-list-item-title v-text="$t(`${service}`)"></v-list-item-title>
            </v-list-item-content>
          </template>
        </v-list-item>
      </v-list>
    </v-form>
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import { companyServicesOptions } from '@/constants/registration';
import { rules } from '@/utils/validationUtils';
import CustomCheckbox from '@/components/common/CustomCheckbox.vue';

export default {
  name: 'CompanyServicesForm',
  props: {
    value: {
      type: Object,
      required: true,
    },
  },
  components: {
    CustomCheckbox,
  },
  data() {
    return {
      servicesChosen: this.value.servicesChosen || [],
      valid: this.value.valid,
      yearsOfExperience: this.value.yearsOfExperience,
    };
  },
  computed: {
    ...mapGetters([
      'spFreelancerServices',
    ]),
    rules() {
      return {
        ...rules,
        // eslint-disable-next-line no-restricted-globals
        isNumber: v => (!isNaN(v) && v >= 0) || this.$t('only.numbers'),
        required: v => rules.required(v) || this.$t('required'),
      };
    },
    companyServicesOptionsFormatted() {
      return Object.values(companyServicesOptions);
    },
    topLevelServicesWithoutSubs() {
      return Object.keys(this.formattedServicesOptions).filter(service => !this.formattedServicesOptions[service].length);
    },
    topLevelServicesWithSubs() {
      return Object.keys(this.formattedServicesOptions).filter(service => this.formattedServicesOptions[service].length);
    },
    formattedServicesOptions() {
      const servicesMap = new Map();
      this.spFreelancerServices.forEach(service => {
        if (service.shortName.includes('.sub.')) {
          const topLevelService = service.shortName.split('.sub.')[0];
          if (!servicesMap.has(topLevelService)) {
            servicesMap.set(topLevelService, [service.shortName]);
          } else {
            const currentSubServices = servicesMap.get(topLevelService);
            currentSubServices.push(service.shortName);
            servicesMap.set(topLevelService, currentSubServices);
          }
        } else if (!servicesMap.has(service.shortName)) {
          servicesMap.set(service.shortName, []);
        }
      });
      return this.mapToObj(servicesMap);
    },
    flatServicesOptionsObject() {
      const flatServicesObject = {};
      this.topLevelServicesWithoutSubs.forEach(
        service => {
          flatServicesObject[service] = this.servicesChosen.includes(service);
        },
      );
      this.topLevelServicesWithSubs.forEach(
        service => {
          this.formattedServicesOptions[service].forEach(
            subService => {
              flatServicesObject[subService] = this.servicesChosen.includes(subService);
            },
          );
        },
      );
      return flatServicesObject;
    },
  },
  methods: {
    onCheck(service) {
      if (!this.servicesChosen.includes(service)) {
        this.servicesChosen.push(service);
      } else {
        const index = this.servicesChosen.indexOf(service);
        this.servicesChosen.splice(index, 1);
      }
    },
    mapToObj(map) {
      const obj = {};
      map.forEach((value, key) => {
        obj[key] = value;
      });
      return obj;
    },
    formChanged() {
      this.valid = this.$refs.form.inputs.every(input => input.valid);
      this.$emit('changed', {
        valid: this.valid,
        yearsOfExperience: this.yearsOfExperience,
        servicesChosen: this.servicesChosen,
      });
    },
  },
  watch: {
    yearsOfExperience() {
      this.formChanged();
    },
    servicesChosen() {
      this.formChanged();
    },
  },
};
</script>

<style lang="scss">
@import '~@/assets/styles/design-system';

.v-text-field--outlined.v-input--dense > .v-input__control > .v-input__slot {
  height: 32px;
}

.vue-tel-input-vuetify .v-text-field--outlined .v-select__selections .vti__flag {
  margin-left: $space-5 !important;
}

.sp-freelancer-form {
  .v-list-item__action {
    margin-right: 0 !important;
  }
  .v-list-item__icon {
    margin-right: 0 !important;
  }
  .v-list-group__items .v-list-item, .v-list-group__items .v-list-group__items {
    margin-left: $space-8;
  }
  .v-list-item__title {
    font-family: $font-family-regular;
    font-size: $font-size-base;
    line-height: $font-size-md;
    letter-spacing: 0;
    color: $auth-text-primary;
  }
  .v-autocomplete {
    input {
      text-overflow: ellipsis;
    }
  }

  .v-text-field__suffix {
    color: $auth-text-primary;
    font-size: $font-size-base;
    letter-spacing: 0;
    line-height: $font-size-md;
    padding-right: $space-2-5;
  }

  .checkbox-title .v-btn {
    padding: 0px $radius-sm;
  }

  .checkbox-title .v-btn__content {
    color: $info-500;
    font-size: $font-size-base;
    letter-spacing: 0;
    line-height: $font-size-md;
    text-transform: capitalize;
  }
}
</style>

<style lang="scss" scoped>
.sp-freelancer-form {
  display: flex;
  flex-direction: column;
  min-width: 356px;

  .v-input--checkbox {
    margin-top: 0px;
  }

  .v-autocomplete {
    input {
      text-overflow: ellipsis;
    }
  }
}

.sp-freelancer-form-row {
  display: flex;
}

.left-input {
  margin-right: 8px;
}

.checkbox-title {
  display: flex;
  align-items: center;

  span {
    font-size: 14px;
    letter-spacing: 0;
    line-height: 16px;
  }
}
</style>
